/**
 * What the `internal` flag means for the person about to press Comment — in
 * words, at the moment they choose.
 *
 * §8.1: "Comments carry an `internal` flag, defaulting to internal. Internal
 * comments never render to client-space audiences **regardless of where the
 * parent node lives**." The emphasis is the whole reason this copy is a module
 * with a test rather than two strings inline. Publishing *moves* the node into
 * the client space (ADR 0004), so the comments travel with the document — which
 * means the moment the flag matters most is while the document is still
 * internal and nobody is thinking about clients at all. A control that only
 * described today's audience would be accurate and useless.
 *
 * The three sentences below are therefore each doing a job:
 *
 * - **Internal** says what it protects, not where the node is.
 * - **Shared, in a client space**, names the space, because "shared" without a
 *   name is a checkbox and the name is the client.
 * - **Shared, in an internal space**, says the quiet part: it is not visible to
 *   anyone outside Matchbox *today*, and publishing is one click away.
 *
 * Nothing here authorizes anything. `seesInternal` mirrors the server's rule in
 * `apps/api/src/comments/visibility.ts` — "does this role's baseline carry
 * `document.edit` at all" — through `roleCarries`, which `components/
 * capabilities.ts` exists to provide for exactly this: explaining a control, not
 * gating a write. A client's comment is forced external by a trigger in
 * migration 0004 whatever this file says.
 */

import type { SpaceKind } from '../../api/types.ts';

export type AudienceValue = 'internal' | 'shared';

export interface AudienceContext {
  /** Whether this person sees internal comments at all, and so may write one. */
  seesInternal: boolean;
  /** Null while the space is still loading — the copy falls back to the safe wording. */
  spaceKind: SpaceKind | null;
  spaceName: string | null;
}

export interface AudienceChoice {
  value: AudienceValue;
  label: string;
  detail: string;
}

export const DEFAULT_AUDIENCE: AudienceValue = 'internal';

export function audienceChoices(context: AudienceContext): AudienceChoice[] {
  const inClientSpace = context.spaceKind === 'client';
  const space = context.spaceName ?? 'the client space';

  return [
    {
      value: 'internal',
      label: 'Internal — Matchbox only',
      detail: inClientSpace
        ? `Not rendered to anyone on the ${space} side, even though the document sits there.`
        : 'Never rendered to a client audience, wherever this document ends up.',
    },
    {
      value: 'shared',
      label: inClientSpace ? `Shared — readable in ${space}` : 'Shared — readable by the client',
      detail: sharedDetail(context),
    },
  ];
}

/**
 * The three wordings, and why the third exists.
 *
 * `spaceKind` is null while the space is still loading — and a form that
 * defaulted to the internal-space sentence in that moment would tell someone
 * "nobody outside Matchbox can read this document today" about a document
 * sitting in a client space. The unknown case therefore claims nothing about
 * today; it is the only one of the three that can be wrong in the dangerous
 * direction, so it is the one that does not guess.
 */
function sharedDetail(context: AudienceContext): string {
  if (context.spaceKind === 'client') {
    return 'Everyone who can read this document, the client included.';
  }
  if (context.spaceKind === 'internal') {
    return (
      'Nobody outside Matchbox can read this document today. Publishing moves it into the ' +
      'client space and this comment goes with it.'
    );
  }
  return (
    'Everyone who can read this document. If it is in a client space already, or is published ' +
    'into one later, that includes the client.'
  );
}

/**
 * The sentence for someone who has no choice to make.
 *
 * A client cannot write an internal comment — the trigger forces the flag — so
 * they get a statement rather than a control. Saying it plainly is the point:
 * the alternative is a person writing what they take to be a private note.
 */
export function audienceNotice(context: AudienceContext): string {
  return context.spaceKind === 'client'
    ? 'Your comment is readable by everyone who can read this document, Matchbox included.'
    : 'Your comment is readable by everyone who can read this document.';
}
